import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingAgentEditability,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

/** Shared vocabulary for the returns settings area. Each returns settings page
 * contributes its own groups and catalog entries but reuses these. */

export const returnsSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.returns",
  product: "returns",
};

export const returnsUiOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.returnsUI",
  product: "returns",
};

export const exchangeSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.exchanges",
  product: "returns",
};

/** Redo-managed returns (warehouse / returns centers) settings subtree. */
export const returnsManagedReturnsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.redoManagedReturns",
  product: "returns",
};

/** Return/claim/warranty flow-builder canvases, persisted as flow models via RPC. */
export const returnsFlowOwner: SettingOwner = {
  kind: "workflow",
  path: "returns/flows",
  product: "returns",
};

/** Assessment templates (custom field forms) managed via the assessments RPCs. */
export const returnsAssessmentsOwner: SettingOwner = {
  kind: "workflow",
  path: "returns/assessments",
  product: "returns",
};

/** Managed returns item outcomes, saved as inventory rules via the managed returns RPCs. */
export const returnsInventoryRulesOwner: SettingOwner = {
  kind: "workflow",
  path: "returns/inventory-rules",
  product: "returns",
};

/** Merchant-operator-only display changes; nothing customer-visible, no money moves. */
export const internalDisplayRisk: SettingRisk = {
  level: "low",
  reason:
    "Only changes what Redo staff and merchant operators see while reviewing returns.",
};

export const customerFacingRisk: SettingRisk = {
  level: "medium",
  reason: "Changes affect what customers see or can do in the return flow.",
};

/** Anything deciding whether money, credit, or inventory moves automatically. */
export const automationMoneyRisk: SettingRisk = {
  level: "high",
  reason:
    "Controls automatic refunds, store credit, and restocking — a wrong value moves money or inventory without review.",
};

export const requiresApproval: SettingAgentEditability = {
  mode: "requiresApproval",
};

export const suggestOnly: SettingAgentEditability = {
  mode: "suggestOnly",
  reason:
    "Return automation decides whether money and inventory move without a human in the loop.",
};

export const returnsAccess = directMerchantAccess(
  SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
);

export const shippingAccess = directMerchantAccess(
  SettingsPermissions.SET_SHIPPING_SETTINGS,
);

/** The withdrawals settings page gates edits on SET_GENERAL_SETTINGS. */
export const withdrawalsAccess = directMerchantAccess(
  SettingsPermissions.SET_GENERAL_SETTINGS,
);

/** Boolean value semantics shared by most returns toggles. */
export const offOn = {
  defaultValue: false,
  falseLabel: "Off",
  trueLabel: "On",
};

export const onOff = { defaultValue: true, falseLabel: "Off", trueLabel: "On" };
